import React, { useId } from 'react';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import InfoIcon from 'assets/info.svg';

interface InfoTooltipProps {
  /** The tooltip content */
  title: React.ReactNode;
  /** Tooltip placement position */
  placement?:
    | 'top'
    | 'left'
    | 'right'
    | 'bottom'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight'
    | 'leftTop'
    | 'leftBottom'
    | 'rightTop'
    | 'rightBottom';

  /** Additional CSS classes for the wrapper span */
  className?: string;

  /** Additional CSS classes for the icon */
  iconClassName?: string;

  /** Additional CSS classes for the tooltip overlay */
  overlayClassName?: string;
}

/**
 * A reusable info tooltip component that displays an info icon with a tooltip on hover
 *
 * @example
 * ```tsx
 * <InfoTooltip title="This is helpful information" />
 *
 * <InfoTooltip
 *   title="Custom tooltip content"
 *   placement="top"
 *   className="ml-2"
 * />
 * ```
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({
  title,
  placement = 'top',
  className = '',
  iconClassName = '',
  overlayClassName = ''
}) => {
  const descriptionId = useId();
  return (
    <>
      <span id={descriptionId} className="sr-only">
        {title}
      </span>
      <Tooltip
        title={title}
        overlayClassName={overlayClassName}
        placement={placement}
        color="#232630"
        trigger={['hover', 'focus']}
      >
        <button
          type="button"
          aria-label="More information"
          aria-describedby={descriptionId}
          className={`inline-flex items-center cursor-default focus-visible:ring-2 focus-visible:ring-blue-60 ${className}`}
          data-testid="info-tooltip"
        >
          <img src={InfoIcon} alt="" className={`w-4 h-4 ${iconClassName}`} />
        </button>
      </Tooltip>
    </>
  );
};

export default InfoTooltip;
